import { useMemo } from 'react';
import { useWizardStore } from '../store/wizardStore';
import { StepShell } from '../components/StepShell';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { MoneyField } from '../components/form/MoneyField';
import { DateField } from '../components/form/DateField';
import { isValidWindows, isValidUnitedWindows, isValidGate, isValidPodryadPrepayment } from '../utils/validators';
import { formatGateTotal, sumGateBreakdown } from '../utils/gateAmounts';
import {
  formatKpTotal,
  formatPodryadTotal,
  formatUnitedTotal,
  sumKpBreakdown,
  sumPodryadBreakdown,
  sumUnitedBreakdown,
} from '../utils/windowsAmounts';
import { rubleAmountToWords } from '../utils/numberToWords';
import type { PaymentMethod, GatePaymentType, WindowsFields, GateFields } from '../types/wizard.types';
import { SIGNING_PERSON_OPTIONS } from '../utils/signingPersons';
import { GATE_SIGNING_PERSON_OPTIONS } from '../utils/gateSigningPersons';

function getMissingWindows(w: WindowsFields, united: boolean): string[] {
  const hasNum = (s: string) => s.trim() !== '' && Number(s.replace(/\s/g, '').replace(',', '.')) >= 0;
  const missing: string[] = [];
  if (!w.contractNumber.trim()) missing.push('Номер договора');
  if (united) {
    if (sumUnitedBreakdown(w) <= 0) missing.push('Сумма договора');
  } else {
    if (sumKpBreakdown(w) <= 0) missing.push('Сумма договора купли-продажи');
    if (sumPodryadBreakdown(w) <= 0) missing.push('Сумма договора подряда');
    if (!isValidPodryadPrepayment(w)) missing.push('Предоплата подряда: укажите сумму от 0 до стоимости работ');
  }
  if (!hasNum(w.prepaymentAmount)) missing.push('Сумма предоплаты');
  if (!/^\d{4}-\d{2}-\d{2}$/.test(w.prepaymentDueDate)) missing.push('Дата оплаты');
  if (w.installationAddress.trim().length < 5) missing.push('Адрес установки');
  return missing;
}

function getMissingGate(g: GateFields): string[] {
  const missing: string[] = [];
  if (!g.contractNumber.trim()) missing.push('Номер договора ворот');
  if (sumGateBreakdown(g) <= 0) missing.push('Состав стоимости договора');
  if (!/^\d{4}-\d{2}-\d{2}$/.test(g.prepaymentDueDate)) missing.push('Дата оплаты ворот');
  if (g.installationAddress.trim().length < 5) missing.push('Адрес установки ворот');
  return missing;
}

const PAYMENT_OPTIONS: Array<{ value: PaymentMethod; label: string }> = [
  { value: 'cash', label: 'Наличные' },
  { value: 'bank', label: 'Расчётный счёт' },
  { value: 'qr', label: 'QR-код' },
];

const ADVANCE_PAYMENT_OPTIONS: Array<{ value: PaymentMethod; label: string }> = [
  { value: 'bank', label: 'перечисление денежных средств на расчетный счет Продавца' },
  { value: 'cash', label: 'внесение наличных денежных средств в кассу Продавца' },
  { value: 'qr', label: 'с использованием QR кода' },
];

const GATE_TYPE_OPTIONS: Array<{ value: GatePaymentType; label: string }> = [
  { value: '50-50', label: '50% + 50%' },
  { value: '50-40-10', label: '50% + 40% + 10%' },
];

const MEASUREMENT_OPTIONS = [
  { value: 'true', label: 'осуществлялся' },
  { value: 'false', label: 'не осуществлялся' },
] as const;

const SIZES_BY_OPTIONS = [
  { value: 'true', label: 'Продавца' },
  { value: 'false', label: 'Покупателя' },
] as const;

export function Step2_YellowForm() {
  const variant = useWizardStore((s) => s.variant);
  const signingPersonId = useWizardStore((s) => s.signingPersonId);
  const setSigningPersonId = useWizardStore((s) => s.setSigningPersonId);
  const gateSigningPersonId = useWizardStore((s) => s.gateSigningPersonId);
  const setGateSigningPersonId = useWizardStore((s) => s.setGateSigningPersonId);
  const windows = useWizardStore((s) => s.windows);
  const gate = useWizardStore((s) => s.gate);
  const setW = useWizardStore((s) => s.setWindowsField);
  const setUnitedBreakdown = useWizardStore((s) => s.setUnitedBreakdownField);
  const setKpBreakdown = useWizardStore((s) => s.setKpBreakdownField);
  const setPodryadBreakdown = useWizardStore((s) => s.setPodryadBreakdownField);
  const setG = useWizardStore((s) => s.setGateField);
  const setGateBreakdown = useWizardStore((s) => s.setGateBreakdownField);
  const goTo = useWizardStore((s) => s.goTo);

  const gateTotalFormatted = useMemo(() => formatGateTotal(gate), [gate]);
  const gateTotalWords = useMemo(() => {
    const total = sumGateBreakdown(gate);
    return total > 0 ? rubleAmountToWords(total) : '';
  }, [gate]);

  const unitedTotalFormatted = useMemo(() => formatUnitedTotal(windows), [windows]);
  const unitedTotalWords = useMemo(() => {
    const total = sumUnitedBreakdown(windows);
    return total > 0 ? rubleAmountToWords(total) : '';
  }, [windows]);

  const kpTotalFormatted = useMemo(() => formatKpTotal(windows), [windows]);
  const kpTotalWords = useMemo(() => {
    const total = sumKpBreakdown(windows);
    return total > 0 ? rubleAmountToWords(total) : '';
  }, [windows]);

  const podryadTotalFormatted = useMemo(() => formatPodryadTotal(windows), [windows]);
  const podryadTotalWords = useMemo(() => {
    const total = sumPodryadBreakdown(windows);
    return total > 0 ? rubleAmountToWords(total) : '';
  }, [windows]);

  const isUnited = variant === 'variant1';
  const isGateOnly = variant === 'variant3';
  const needsWindows = variant === 'variant1' || variant === 'variant2';

  const windowsOk = needsWindows
    ? (isUnited ? isValidUnitedWindows(windows) : isValidWindows(windows))
    : true;
  const gateOk = isGateOnly ? isValidGate(gate) : true;
  const canProceed = windowsOk && gateOk;

  const missingWindows = (needsWindows && !windowsOk) ? getMissingWindows(windows, isUnited) : [];
  const missingGate = (isGateOnly && !gateOk) ? getMissingGate(gate) : [];

  return (
    <StepShell
      title="Данные сотрудника"
      subtitle="Заполните поля и передайте планшет клиенту"
      current="yellow"
      footer={
        <div className="space-y-2">
          {missingWindows.length > 0 && (
            <p className="text-xs text-amber-700 text-right">
              Окна — не заполнено: {missingWindows.join(' · ')}
            </p>
          )}
          {missingGate.length > 0 && (
            <p className="text-xs text-amber-700 text-right">
              Ворота — не заполнено: {missingGate.join(' · ')}
            </p>
          )}
          <div className="flex justify-between">
            <Button variant="secondary" onClick={() => goTo('variant')}>
              Назад
            </Button>
            <Button disabled={!canProceed} onClick={() => goTo('green')}>
              Передать планшет клиенту →
            </Button>
          </div>
        </div>
      }
    >
      {needsWindows && (
        <section className="section-yellow space-y-4">
          <h2 className="text-lg font-semibold">
            {variant === 'variant1'
              ? 'Окна — объединённый договор (ООО «Ворота-Окна»)'
              : 'Окна — договор купли-продажи и подряда'}
          </h2>

          <Select
            label="Выбор представителя *"
            value={signingPersonId}
            onValueChange={setSigningPersonId}
            options={SIGNING_PERSON_OPTIONS}
          />
          {signingPersonId === 'office' && variant === 'variant2' && (
            <p className="text-sm text-slate-600">
              В договоре купли-продажи — генеральный директор ООО «Ворота-Окна» (Коновалова Е.Ю.);
              в договоре подряда — ИП Федина Е.И. На шаге подписания потребуются две отдельные подписи сотрудника.
            </p>
          )}

          <div className="grid md:grid-cols-2 gap-4">
            <Input
              label="Номер договора *"
              value={windows.contractNumber}
              onChange={(e) => setW('contractNumber', e.target.value)}
            />
            <DateField
              label="Дата договора"
              value={windows.contractDate}
              onChange={(v) => setW('contractDate', v)}
              required
            />
          </div>

          {isUnited ? (
            <>
              <h3 className="font-medium">3.4 Сумма договора составляет:</h3>
            <div className="space-y-4">
              <div className="rounded-xl border bg-slate-50 px-4 py-3 max-w-xl">
                <p className="text-sm text-slate-500">1. Общая сумма договора</p>
                <p className="text-lg font-semibold mt-1">
                  {unitedTotalFormatted ? `${unitedTotalFormatted} руб.` : '—'}
                </p>
                {unitedTotalWords && (
                  <p className="text-xs text-slate-600 mt-1 italic">{unitedTotalWords}</p>
                )}
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <MoneyField
                  label="2. Стоимость конструкций и комплектующих к ним"
                  value={windows.constructionsAmount}
                  onChange={(v) => setUnitedBreakdown('constructionsAmount', v)}
                />
                <MoneyField
                  label="3. Стоимость внутренних откосов"
                  value={windows.slopesAmount}
                  onChange={(v) => setUnitedBreakdown('slopesAmount', v)}
                />
                <MoneyField
                  label="4. Стоимость доставки, разгрузки и подъема"
                  value={windows.deliveryAmount}
                  onChange={(v) => setUnitedBreakdown('deliveryAmount', v)}
                />
                <MoneyField
                  label="5. Стоимость работ сборке и монтажу Конструкций"
                  value={windows.workInstallation}
                  onChange={(v) => setUnitedBreakdown('workInstallation', v)}
                />
                <MoneyField
                  label="6. Стоимость работ по установке внутренних откосов"
                  value={windows.workSlopes}
                  onChange={(v) => setUnitedBreakdown('workSlopes', v)}
                />
                <MoneyField
                  label="7. Стоимость работ по вывозу мусора"
                  value={windows.workAdditional}
                  onChange={(v) => setUnitedBreakdown('workAdditional', v)}
                />
              </div>
            </div>
            </>
          ) : (
            <div className="space-y-4">
              <h3 className="font-medium">Суммы по договору купли-продажи</h3>
              <div className="rounded-xl border bg-slate-50 px-4 py-3 max-w-xl">
                <p className="text-sm text-slate-500">3.4. Сумма договора составляет</p>
                <p className="text-lg font-semibold mt-1">
                  {kpTotalFormatted ? `${kpTotalFormatted} руб.` : '—'}
                </p>
                {kpTotalWords && (
                  <p className="text-xs text-slate-600 mt-1 italic">{kpTotalWords}</p>
                )}
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <MoneyField
                  label="Общая стоимость Конструкций и комплектующих"
                  value={windows.constructionsAmount}
                  onChange={(v) => setKpBreakdown('constructionsAmount', v)}
                />
                <MoneyField
                  label="Стоимость работ по вывозу мусора"
                  value={windows.garbageRemovalAmount}
                  onChange={(v) => setKpBreakdown('garbageRemovalAmount', v)}
                />
              </div>
            </div>
          )}

          <h3 className="font-medium">Предоплата</h3>
          <div className="grid md:grid-cols-3 gap-4">
            <MoneyField label="Сумма предоплаты" value={windows.prepaymentAmount} onChange={(v) => setW('prepaymentAmount', v)} required />
            <DateField label="Оплатить до" value={windows.prepaymentDueDate} onChange={(v) => setW('prepaymentDueDate', v)} required />
            <Select label="Способ оплаты" value={windows.paymentMethod} options={PAYMENT_OPTIONS} onValueChange={(v) => setW('paymentMethod', v)} />
          </div>

          {!isUnited && (
            <div className="space-y-4">
              <h3 className="font-medium">Суммы по договору подряда</h3>
              <Select
                label="Предоплата по договору подряда ИП Фединой"
                value={windows.podryadPrepaymentMode}
                options={[
                  { value: 'half', label: '50% от стоимости работ' },
                  { value: 'none', label: 'Без предоплаты' },
                  { value: 'custom', label: 'Указать сумму вручную' },
                ]}
                onValueChange={(v) => setW('podryadPrepaymentMode', v)}
              />
              {windows.podryadPrepaymentMode === 'custom' && (
                <MoneyField label="Сумма предоплаты по договору подряда" value={windows.podryadPrepaymentAmount}
                  onChange={(v) => setW('podryadPrepaymentAmount', v)} required />
              )}
              {!isValidPodryadPrepayment(windows) && (
                <p className="text-sm text-red-700">Укажите сумму от 0 до полной стоимости работ по договору подряда.</p>
              )}
              <p className="text-sm text-slate-600">
                Предоплата по подряду: {new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB' }).format(
                  windows.podryadPrepaymentMode === 'none' ? 0 : windows.podryadPrepaymentMode === 'custom'
                    ? Number(windows.podryadPrepaymentAmount.replace(/\s/g, '').replace(',', '.')) || 0
                    : Math.round(sumPodryadBreakdown(windows) * 50) / 100
                )}. Оставшаяся сумма оплачивается после завершения монтажа.
              </p>
              <div className="rounded-xl border bg-slate-50 px-4 py-3 max-w-xl">
                <p className="text-sm text-slate-500">2.1. Стоимость работ по договору подряда</p>
                <p className="text-lg font-semibold mt-1">
                  {podryadTotalFormatted ? `${podryadTotalFormatted} руб.` : '—'}
                </p>
                {podryadTotalWords && (
                  <p className="text-xs text-slate-600 mt-1 italic">{podryadTotalWords}</p>
                )}
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <MoneyField label="Демонтаж старых конструкций" value={windows.workDemontage} onChange={(v) => setPodryadBreakdown('workDemontage', v)} />
                <MoneyField
                  label="Монтаж оконных/дверных конструкций с заполнением монтажного шва по периметру"
                  value={windows.workInstallation}
                  onChange={(v) => setPodryadBreakdown('workInstallation', v)}
                />
                <MoneyField label="Стоимость внутренних откосов" value={windows.workSlopes} onChange={(v) => setPodryadBreakdown('workSlopes', v)} />
                <MoneyField label="Стоимость установки нащельников" value={windows.workFlashings} onChange={(v) => setPodryadBreakdown('workFlashings', v)} />
                <MoneyField label="Дополнительные работы" value={windows.workAdditional} onChange={(v) => setPodryadBreakdown('workAdditional', v)} />
              </div>
            </div>
          )}

          <Input
            label="Адрес установки (п. 5.3) *"
            value={windows.installationAddress}
            onChange={(e) => setW('installationAddress', e.target.value)}
          />

          <div className="grid md:grid-cols-2 gap-4">
            <Select
              label="1.5. Замер проёмов для установки Конструкций Продавцом"
              value={windows.measurementDone ? 'true' : 'false'}
              options={[...MEASUREMENT_OPTIONS]}
              onValueChange={(v) => setW('measurementDone', v === 'true')}
            />
            <Select
              label="1.6. Конструкции изготавливаются по размерам"
              value={windows.sizesBySeller ? 'true' : 'false'}
              options={[...SIZES_BY_OPTIONS]}
              onValueChange={(v) => setW('sizesBySeller', v === 'true')}
            />
          </div>
        </section>
      )}

      {/* Блок ворот — только для variant3 */}
      {isGateOnly && (
        <section className="section-yellow space-y-4">
          <h2 className="text-lg font-semibold">Ворота — договор купли-продажи (ИП Шалеев А.Г.)</h2>

          <Select
            label="Выбор представителя *"
            value={gateSigningPersonId}
            onValueChange={setGateSigningPersonId}
            options={GATE_SIGNING_PERSON_OPTIONS}
          />

          <div className="grid md:grid-cols-2 gap-4">
            <Input label="Номер договора ворот *" value={gate.contractNumber} onChange={(e) => setG('contractNumber', e.target.value)} />
            <DateField
              label="Дата договора"
              value={gate.contractDate}
              onChange={(v) => setG('contractDate', v)}
              required
            />
          </div>

          <h3 className="font-medium">Состав стоимости договора (таблица п. 5.1)</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <MoneyField
              label="1. Продукция (воротные системы, автоматика и комплектующие)"
              value={gate.productAmount}
              onChange={(v) => setGateBreakdown('productAmount', v)}
            />
            <MoneyField
              label="2. Материал для подготовки проёма"
              value={gate.preparationMaterial}
              onChange={(v) => setGateBreakdown('preparationMaterial', v)}
            />
            <MoneyField
              label="3. Доставка, разгрузка и подъём"
              value={gate.deliveryAmount}
              onChange={(v) => setGateBreakdown('deliveryAmount', v)}
            />
            <MoneyField
              label="4. Монтаж воротных систем и пуско-наладка"
              value={gate.installationAmount}
              onChange={(v) => setGateBreakdown('installationAmount', v)}
            />
            <MoneyField
              label="5. Работы по подготовке проёма"
              value={gate.preparationWork}
              onChange={(v) => setGateBreakdown('preparationWork', v)}
            />
            <MoneyField
              label="6. Демонтаж старых ворот, вывоз мусора"
              value={gate.demontageAmount}
              onChange={(v) => setGateBreakdown('demontageAmount', v)}
            />
          </div>

          <div className="rounded-xl border bg-slate-50 px-4 py-3 max-w-xl">
            <p className="text-sm text-slate-500">Итого по договору</p>
            <p className="text-lg font-semibold mt-1">
              {gateTotalFormatted ? `${gateTotalFormatted} руб.` : '—'}
            </p>
            {gateTotalWords && (
              <p className="text-xs text-slate-600 mt-1 italic">{gateTotalWords}</p>
            )}
          </div>

          <h3 className="font-medium">3. Оплата</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <Select label="Схема оплаты" value={gate.paymentType} options={GATE_TYPE_OPTIONS} onValueChange={(v) => setG('paymentType', v)} />
            <MoneyField label="Предоплата (50%)" value={gate.prepaymentAmount} onChange={(v) => setG('prepaymentAmount', v)} />
            <Select
              label="Авансовый платеж внесен следующим способом"
              value={gate.paymentMethod}
              options={ADVANCE_PAYMENT_OPTIONS}
              onValueChange={(v) => setG('paymentMethod', v)}
            />
            <DateField label="Предоплата до" value={gate.prepaymentDueDate} onChange={(v) => setG('prepaymentDueDate', v)} required />
          </div>
          <Input
            label="Адрес установки ворот *"
            value={gate.installationAddress}
            onChange={(e) => setG('installationAddress', e.target.value)}
          />
          <div className="grid md:grid-cols-2 gap-4">
            <Select
              label="Замер проёмов для установки Продукции Продавцом"
              value={gate.measurementDone ? 'true' : 'false'}
              options={[...MEASUREMENT_OPTIONS]}
              onValueChange={(v) => setG('measurementDone', v === 'true')}
            />
            <Select
              label="Продукция изготавливается по размерам"
              value={gate.sizesBySeller ? 'true' : 'false'}
              options={[...SIZES_BY_OPTIONS]}
              onValueChange={(v) => setG('sizesBySeller', v === 'true')}
            />
          </div>
        </section>
      )}
    </StepShell>
  );
}
